import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { equalsCore, highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';
import { OrdersState } from 'src/app/shared/states/orders/orders.state';
import { GetStates } from 'src/app/shared/states/profile/profile.actions';
import { ProfileState } from 'src/app/shared/states/profile/profile.state';
import { IOrderPartyDetailForm } from '../../interfaces/collect-order-party-details-form.interface';
import { OrderPartyDetail, OrderPartyTypeEnum } from '../../interfaces/order-party-details.interface';

@UntilDestroy()
@Component({
  selector: 'app-add-edit-order-party',
  templateUrl: './add-edit-order-party.component.html',
  styleUrls: ['./add-edit-order-party.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddEditOrderPartyComponent implements OnInit {
  private _data!: OrderPartyDetail;

  @Input()
  get data(): OrderPartyDetail {
    return this._data;
  }
  set data(value: OrderPartyDetail) {
    this._data = value;
    this.isEditMode = !!value?.id;
  }

  @Input() type: OrderPartyTypeEnum = OrderPartyTypeEnum.Supplier;

  @Output() submitted: EventEmitter<OrderPartyDetail> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  OrderPartyTypeEnum = OrderPartyTypeEnum;

  header = '';
  companyNameValidatorError;

  form: FormGroup<IOrderPartyDetailForm>;
  shippingAddressIsSameWithPhysical: boolean;

  isEditMode: boolean;
  private editModeOriginalFormDataSnapshot: any;

  @Select(ProfileState.states) states$: Observable<string[]>;
  @Select(ProfileState.loading) loadingProfile$: Observable<boolean>;
  @Select(OrdersState.loading) loadingOrder$: Observable<boolean>;


  @ViewChild('addEditPartyDialog') addEditPartyDialog;
  @ViewChild('companyName') companyName;
  @ViewChild('dea') dea;
  @ViewChild('shippingAddress1') shippingAddress1;

  /**
   *
   */
  constructor(private fb: FormBuilder, private store: Store, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.initForm();
    
    this.header = `${this.isEditMode? 'Edit ' : 'Add New'} ${this.type === OrderPartyTypeEnum.Buyer || this.type === OrderPartyTypeEnum.EdiBuyer ? 'Buyer' : 'Supplier'}`;
    //EdiBuyer and Supplier is customer's pharmacy 
    if (this.type === OrderPartyTypeEnum.EdiBuyer) {
      this.type = OrderPartyTypeEnum.Supplier;
    }

    if(!this.store.selectSnapshot(ProfileState.states)?.length) {
      this.store.dispatch(new GetStates());
    }
    this.form.patchValue(this.data);
    this.editModeOriginalFormDataSnapshot = this.form.getRawValue();
  }

  submitForm(): void {
   if (this.form.valid) {
    this.form.get('orderParty').get('type').patchValue(this.type);
    if (this.shippingAddressIsSameWithPhysical) {
      this.form.get('shippingAddress').patchValue(this.form.get('physicalAddress').getRawValue());
    }
    //save only modified
    if (!equalsCore(this.form.getRawValue(), this.editModeOriginalFormDataSnapshot)) {
      this.submitted.next(this.form.getRawValue());
    } else this.cancel.next();
   } else {
    highlightRequiredControlsWithErrors(this.form);
   }
  }

  checkBoxValueChanged(): void {
    this.shippingAddressIsSameWithPhysical = !this.shippingAddressIsSameWithPhysical;
    this.form.get('shippingAddress').get('address1').updateValueAndValidity();
    this.form.get('shippingAddress').get('zipCode').updateValueAndValidity();
    this.form.get('shippingAddress').get('state').updateValueAndValidity();
    this.form.get('shippingAddress').get('city').updateValueAndValidity();
  }


  private shippingAddressValidator(control: FormControl): any {
    if (!this.shippingAddressIsSameWithPhysical && !(!!control.value)) {
      return { required: true };
    }
    return null;
  }

  private companyNameValidator(control: FormControl): {[s:string]: boolean} | null {
    const inputVal = control?.value?.toLowerCase();
    if (!control.value) {
      this.companyNameValidatorError = 'This field is required';
      return { required : true };
    } 
    this.companyNameValidatorError = 'Company already exists';
    const nameTaken = (array: any[]) => array?.some(x => x?.companyName?.toLowerCase() === inputVal);

    if (!this.isEditMode && this.type === OrderPartyTypeEnum.EdiSupplier && nameTaken(this.store.selectSnapshot(OrdersState.ediSuppliers))) {
      return { duplicateName : true };
    }  
    if (!this.isEditMode && this.type === OrderPartyTypeEnum.Buyer && nameTaken(this.store.selectSnapshot(OrdersState.buyers))) { //new entity mode
      return { duplicateName : true };
    }
    return null;
  }

  handleLastInputTabbing(): void {
    if (this.form.invalid) {
      this.addEditPartyDialog.focusCancelButton();
    } else {
      this.addEditPartyDialog.focusSubmitButton();
    } 
  }

  handleControlsTabbing(): void {
    if (this.isEditMode && this.type !== OrderPartyTypeEnum.Supplier) {
      this.dea.focusInput();
    } else if (this.isEditMode && this.type === OrderPartyTypeEnum.Supplier) {
      this.shippingAddress1.focusInput();
    } else {
      this.companyName.focusInput();
    }
  }

  handleShippingZipCodeTabbing(): void {
    if (this.type === OrderPartyTypeEnum.Supplier) {
      event.preventDefault();
      this.form.valid ? this.addEditPartyDialog.focusSubmitButton() : this.addEditPartyDialog.focusCancelButton();
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      orderParty: this.fb.group({
        companyName: ['', this.companyNameValidator.bind(this)],
        type: [this.type, Validators.required],
      }),
      deaNumber: ['', Validators.required],
      physicalAddress: this.fb.group({
        address1: ['', Validators.required],
        address2: '',
        zipCode: ['', Validators.required],
        state: ['', Validators.required],
        city: ['', Validators.required],
      }),
      shippingAddress: this.fb.group({
        address1: ['', this.shippingAddressValidator.bind(this)],
        address2: '',
        zipCode: ['', this.shippingAddressValidator.bind(this)],
        state: ['', this.shippingAddressValidator.bind(this)],
        city: ['', this.shippingAddressValidator.bind(this)],
      }),
      contactPerson: this.fb.group({
        firstName: ['', this.type !== OrderPartyTypeEnum.Supplier && this.type !== OrderPartyTypeEnum.EdiBuyer ? Validators.required: Validators.nullValidator],
        lastName: ['', this.type !== OrderPartyTypeEnum.Supplier && this.type !== OrderPartyTypeEnum.EdiBuyer ? Validators.required: Validators.nullValidator],
      }),
   });
  }

}
